import { useState } from 'react';
import { useMyEnrollments, useEnrollmentCounts } from '@/api/enrollments';
import { PositionFilter } from '@/components/shared/position-filter';
import { LoadingSpinner } from '@/components/shared/loading-spinner';
import { ErrorMessage } from '@/components/shared/error-message';
import { EmptyState } from '@/components/shared/empty-state';
import { Pagination } from '@/components/shared/pagination';
import { Link } from 'react-router';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatPercent } from '@/lib/formatters';
import { EnrollmentCard } from './enrollment-card';
import type { EnrollmentStatus } from '@/types';

type TabFilter = EnrollmentStatus | 'all_active';

const STATUS_TABS: { label: string; value: TabFilter }[] = [
  { label: 'Todos', value: 'all_active' },
  { label: 'En Progreso', value: 'active' },
  { label: 'Completados', value: 'completed' },
  { label: 'Vencidos', value: 'expired' },
  { label: 'Cancelados', value: 'cancelled' },
];

const STATUS_LABELS: Record<EnrollmentStatus, string> = {
  active: 'En Progreso',
  completed: 'Completado',
  expired: 'Vencido',
  cancelled: 'Cancelado',
};

const CATEGORY_BORDER: Record<string, string> = {
  induction: 'border-t-blue-500',
  periodic: 'border-t-purple-500',
  document_change: 'border-t-amber-500',
  general: 'border-t-gray-400',
};

export function CoursesPage() {
  const [tabFilter, setTabFilter] = useState<TabFilter>('all_active');
  const [page, setPage] = useState(1);
  const [positionId, setPositionId] = useState<number | undefined>(undefined);

  const apiStatus: EnrollmentStatus | undefined =
    tabFilter === 'all_active' ? undefined : tabFilter;
  const excludeCancelled = tabFilter === 'all_active';

  const {
    data: enrollmentsRes,
    isLoading,
    error,
  } = useMyEnrollments({
    status: apiStatus,
    exclude_status: excludeCancelled ? 'cancelled' : undefined,
    page,
    per_page: 9,
    position_id: positionId,
  });

  const { data: counts } = useEnrollmentCounts(positionId);

  const enrollments = enrollmentsRes?.data ?? [];
  const pagination = enrollmentsRes?.meta?.pagination;

  // "Continue" banner: highest-progress active enrollment on first page
  const showContinueBanner = (tabFilter === 'all_active' || tabFilter === 'active') && page === 1;
  const continueEnrollment = showContinueBanner
    ? ([...enrollments]
        .filter((e) => e.status === 'active' && e.progress_percent > 0)
        .sort((a, b) => b.progress_percent - a.progress_percent)[0] ?? null)
    : null;
  const gridEnrollments = continueEnrollment
    ? enrollments.filter((e) => e.id !== continueEnrollment.id)
    : enrollments;

  function getTabLabel(tab: (typeof STATUS_TABS)[number]) {
    if (!counts) return tab.label;
    if (tab.value === 'all_active') {
      const total = (counts.active ?? 0) + (counts.completed ?? 0) + (counts.expired ?? 0);
      return total > 0 ? `${tab.label} (${total})` : tab.label;
    }
    const c = counts[tab.value] ?? 0;
    return c > 0 ? `${tab.label} (${c})` : tab.label;
  }

  function handleStatusChange(status: TabFilter) {
    setTabFilter(status);
    setPage(1);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Mi Capacitación</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Revisa tus cursos asignados y tu progreso de aprendizaje.
        </p>
      </div>

      <PositionFilter value={positionId} onChange={setPositionId} />

      {/* Status filter tabs */}
      <div className="flex gap-1 rounded-lg border border-border bg-muted/50 p-1">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.label}
            onClick={() => handleStatusChange(tab.value)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              tabFilter === tab.value
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {getTabLabel(tab)}
          </button>
        ))}
      </div>

      {/* Content */}
      {isLoading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorMessage message="No se pudieron cargar los cursos." />
      ) : enrollments.length === 0 ? (
        <EmptyState
          title="Sin cursos"
          description={
            tabFilter !== 'all_active'
              ? `No tienes cursos con estado "${STATUS_LABELS[tabFilter]}".`
              : 'No tienes cursos asignados actualmente.'
          }
        />
      ) : (
        <>
          {/* Continue banner */}
          {continueEnrollment && (
            <Link
              to={`/courses/${continueEnrollment.id}`}
              className={cn(
                'group flex items-center justify-between gap-4 rounded-xl border border-primary/30 border-t-4 bg-primary/5 p-4 transition-shadow hover:shadow-md',
                CATEGORY_BORDER[continueEnrollment.course.category] ?? CATEGORY_BORDER.general,
              )}
            >
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-muted-foreground">Continúa tu curso</p>
                <h3 className="mt-0.5 truncate text-base font-bold text-card-foreground group-hover:text-primary">
                  {continueEnrollment.course.title}
                </h3>
                <div className="mt-2 flex items-center gap-3">
                  <div className="h-2 w-32 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-primary transition-all"
                      style={{ width: `${continueEnrollment.progress_percent}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium text-muted-foreground">
                    {formatPercent(continueEnrollment.progress_percent)}
                  </span>
                </div>
              </div>
              <span className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">
                Continuar <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          )}

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {gridEnrollments.map((enrollment) => (
              <EnrollmentCard key={enrollment.id} enrollment={enrollment} />
            ))}
          </div>

          {pagination && <Pagination meta={pagination} onPageChange={setPage} />}
        </>
      )}
    </div>
  );
}
